import { isDataSource } from '@angular/cdk/collections';
import { Injector } from '@angular/core';
import { Observable, of } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { isNullOrUndefined } from 'util';
import { ISuccessItem, BulkUpdateResponse } from '../../bsshapi/api-wrappers';
import IResource from '../../model/resource';
import { IUser } from '../../model/user';
import { IApiResponseWrapper, IErrorState, IResourceReference } from '../../model/v2-api/v2-api-wrappers';
import { ArchiveService } from '../../services/toolbar/archive.service';
import { RestoreService } from '../../services/toolbar/restore.service';
import { AbstractResourceStore } from './abstract.resource.store';
import { IResourceState } from './resource.state';
import { FileStatus } from '@app/core/model/file-status';


export abstract class ResourceCommonUtilityStore<T extends IResourceState> extends AbstractResourceStore<T> {
    /* This is named as a store. But more or less, it works as an utility */

    private archiveServ: ArchiveService;
    private reStoreServ: RestoreService;

    constructor(protected stateSlices: Array<keyof T>, protected injector: Injector) {
        super(stateSlices);
        this.archiveServ = injector.get(ArchiveService);
        this.reStoreServ = injector.get(RestoreService);
    }

    /* @overide in individual stores if needed*/
    getResourceId(resource: IResource): string {
        return resource.Id;
    }

    /* @overide in individual stores */
    getArchiveResponse(resourceIds: string[]): Observable<IApiResponseWrapper<BulkUpdateResponse>> {
        return of(null);
    }

    /* @overide in individual stores */
    getRestoreResponse(resourceIds: string[]): Observable<IApiResponseWrapper<BulkUpdateResponse>> {
        return of(null);
    }

    isArchiveEnabled(): boolean {
        const activeResources = this.getActiveList();
        const { currentUser } = this.getState();
        return this.archiveServ.canArchive(activeResources, currentUser);
    }

    getArchivableResources(): IResource[] {
        const activeResources = this.getActiveList();
        const { currentUser } = this.getState();
        const archivableResources: IResource[] = this.filterArchivableResources(activeResources, currentUser) ;
        return archivableResources;
    }

    getRestorableResources(): IResource[] {
        const activeResources = this.getActiveList();
        const { currentUser } = this.getState();
        const restorableResources: IResource[] = this.filterRestorableResources(activeResources, currentUser) ;
        return restorableResources;
    }

    /* Either Runs or Datasets only can be archived */
    archiveResources(resources: IResource[]) {
        this.loadingSubject.next(true);
        const ids = [];
        resources.forEach(resource => {
            ids.push(this.getResourceId(resource));
        });
        return this.getArchiveResponse(ids).pipe(
            finalize(() => {
                this.loadingSubject.next(false);
            }));
    }

    /* Either Runs or Datasets only can be archived */
    restoreResources(resources: IResource[]) {
        this.loadingSubject.next(true);
        const ids = [];
        resources.forEach(resource => {
            ids.push(this.getResourceId(resource));
        });
        return this.getRestoreResponse(ids).pipe(
            finalize(() => {
                this.loadingSubject.next(false);
            }));
    }

    /* @overide in individual stores if needed*/
    filterArchivableResources(resources: IResource[], user: IUser): IResource[] {
        const isArchivable = (resource: IResource) => {
            return !isNullOrUndefined(resource) && !resource.IsArchived && resource.UserOwnedBy &&
                resource.UserOwnedBy.Id === user.Id && this.isResourceDataValid(resource);
        };

        return resources.filter(resource => isArchivable(resource));
    }

    /* @overide in individual stores if needed*/
    filterRestorableResources(resources: IResource[], user: IUser): IResource[] {
       const invalidStatuses: string[] = [FileStatus.ARCHIVING, FileStatus.UNARCHIVING];
        const isRestorable = (resource: IResource) => {
            return !isNullOrUndefined(resource) && resource.IsArchived && resource.UserOwnedBy &&
            (resource.DataStatuses == null || (resource.DataStatuses.every(ds => !invalidStatuses.includes(ds)))) &&
            resource.UserOwnedBy.Id === user.Id && this.isResourceDataValid(resource);
          };

        return resources.filter(resource => isRestorable(resource));
    }

    /* only runs or datasets can be archived */
    processApiResponse(resourcesToArchive: IResource[], apiResponse: IApiResponseWrapper<BulkUpdateResponse>,
                       errorMsgToDisplay?: string): IProccessedArchiveOrRestoreApiAresponse {
        let successfulResources = [];
        if (apiResponse.Response.TotalSuccessCount > 0) {
            successfulResources = resourcesToArchive.filter(resource => {
                const idToCheck = this.getResourceId(resource);
                return this.isResourceArchivedOrRestoredSuccessfully(apiResponse.Response, idToCheck);
            });
        }

        let archiveOrRestoreError: IErrorState = null;
        if (apiResponse.Response.TotalFailureCount > 0) {
            archiveOrRestoreError = {
                errorMessage: errorMsgToDisplay,
                data: apiResponse.Response.FailureItems
            };
        }
        return {
            totalSuccessCount: apiResponse.Response.TotalSuccessCount,
            successfulResources,
            totalFailureCount: apiResponse.Response.TotalFailureCount,
            failureError: archiveOrRestoreError
        };
    }

    private isResourceDataValid(resource: IResource): boolean {
        if (!resource) {
            return false;
        }

        return resource.TotalSize > 0 && !resource.IsDeleted && !resource.IsFileDataDeleted;
    }

    private isResourceArchivedOrRestoredSuccessfully(apiResponse: BulkUpdateResponse, resourceId: string): boolean {
        const successItems: ISuccessItem[] = apiResponse.SuccessItems;
        if (isNullOrUndefined(successItems)) {
            return false;
        }
        return successItems.some(successItem => successItem.Id === resourceId);
    }

    getUpdatedResourceList(resource: IResource, resourceList: IResource[], propertyUpdate: object): IResource[] {
        if (!isNullOrUndefined(resourceList)) {
          const idxSelectedResource = resourceList.findIndex(r => this.getResourceId(r) === this.getResourceId(resource));
          if (!isNullOrUndefined(idxSelectedResource) && idxSelectedResource !== -1) {
            resourceList[idxSelectedResource] = Object.assign(resourceList[idxSelectedResource], propertyUpdate);
          }
        }

        return resourceList;
    }

    isRestoreEnabled(): boolean {
        const activeResources = this.getActiveList();
        const { currentUser } = this.getState();
        return this.reStoreServ.canRestore(activeResources, currentUser);
    }
}

export interface IProccessedArchiveOrRestoreApiAresponse {
    totalSuccessCount: number;
    successfulResources: IResource[];
    totalFailureCount: number;
    failureError: IErrorState;
}
